<script>
    import { keyScope } from "../scripts/keyboard";
    import ActionButton from "./components/ActionButton.svelte";
    import PlaygroundFeatureList from "./components/PlaygroundFeatureList.svelte";
    import PlaygroundEditFeatureList from "./components/PlaygroundEditFeatureList.svelte";
    import PlaygroundPhotoGallery from "./components/PlaygroundPhotoGallery.svelte";
    import PopupHeader from "./components/PopupHeader.svelte";
    import ApiClient from "../scripts/apiclient";
    import { createActionRunner } from "../scripts/ActionRunner";
    import OperatorDropdown from "./components/OperatorDropdown.svelte";

    const { busy, run } = createActionRunner();

    export let closePopup; // Function to close the surrounding popup
    export let playground;
    let modifiedPlayground = { ...playground };
    let operatorId = getOperatorId(modifiedPlayground);

    /**
     * Get the right operator id to use in the dialog. Regular playgrounds have a populated operator with an id. Draft
     * playgrounds being imported just have a string id.
     *
     * @param playground The playground to peek into.
     */
    function getOperatorId(playground) {
        if (!playground || !playground.operator) {
            return null;
        }

        if (
            typeof playground.operator == "string" &&
            playground.operator.length == 24
        ) {
            return playground.operator;
        }

        if (
            typeof playground.operator == "object" &&
            !!playground.operator._id
        ) {
            return playground.operator._id;
        }

        return null;
    }

    const reject = run(() => {
        // Imported draft playgrounds have never been stored, so rejecting one is just a
        // matter of dropping it. Marking it as rejected is what tells the sidebar that the
        // item was handled. Everything else is rejected on the server.
        if (!modifiedPlayground._id) {
            modifiedPlayground.state = "rejected";
            close(modifiedPlayground);
            return;
        }

        return ApiClient.rejectPlayground(modifiedPlayground).then(close);
    });

    const approve = run(async () => {
        modifiedPlayground.operator = operatorId;

        if (!modifiedPlayground._id) {
            // If we're "approving" an imported draft playground, treat it as a completely
            // new one. The state is sent rather than set here, since the dialog is still
            // showing a draft until the server has accepted it. It has never been to the
            // server, so there are no photos waiting for it either.
            let created = await ApiClient.createPlayground({
                ...modifiedPlayground,
                state: "published",
            });

            return close(created);
        }

        let updated = await ApiClient.approvePlayground(modifiedPlayground);

        // The playground goes first, so that a failure here can never leave published
        // photos on a playground that isn't. Photos left behind this way aren't lost
        // either: once their playground is published they turn up in the sidebar as
        // items of their own.
        await publishPendingPhotos();

        close(updated);
    });

    /**
     * Publishes the photos that came in with the playground.
     *
     * Photos sent along with a new playground stay out of sight until it is approved, since
     * the sidebar only lists photos of published playgrounds. Publishing them along with it
     * saves having to come back for them once the playground is through, and any that don't
     * belong can be rejected afterwards from the popup of the published playground.
     */
    async function publishPendingPhotos() {
        let photos = await ApiClient.loadPhotos(modifiedPlayground._id);

        // Both draft and published photos are loaded, and the published ones are already
        // where they should be
        let pending = photos.filter((photo) => photo.state == "draft");

        if (pending.length == 0) {
            return;
        }

        await ApiClient.approvePhotos(pending);
    }

    const save = run(() => {
        modifiedPlayground.operator = operatorId;

        if (modifiedPlayground._id) {
            return ApiClient.updatePlayground(modifiedPlayground).then(close);
        }

        return ApiClient.createPlayground(modifiedPlayground).then(close);
    });

    // This wrapper prevents the mouse event to be sent to close()
    function cancel() {
        close();
    }

    // Closing is possible while an action is running, so that a hanging action can be
    // abandoned and retried. The flag makes sure that an action finishing after that
    // doesn't close the popup a second time, closing whatever has been opened since.
    let closed = false;

    function close(updatedPlayground) {
        if (closed || !closePopup) {
            return;
        }

        if (updatedPlayground) {
            Object.assign(playground, updatedPlayground);
        }

        closed = true;
        closePopup();
    }

    keyScope(
        {
            Enter: () =>
                modifiedPlayground.state == "draft" ? approve() : save(),
            Escape: cancel,
            a: () => {
                if (modifiedPlayground.state == "draft") {
                    reject();
                }
            },
        },
        { modal: true }
    );
</script>

<div class="dialog">
    <div class="header">
        <PopupHeader {playground} showRating={false} />
    </div>

    <div class="fields">
        <div class="form-group">
            <label for="name">Namn</label>
            <input
                type="text"
                name="name"
                class="form-control-sm"
                bind:value={modifiedPlayground.name}
            />

            <label for="description">Beskrivning</label>
            <textarea
                name="description"
                class="form-control-sm"
                bind:value={modifiedPlayground.description}
            />

            <label for="food">Mat</label>
            <input
                type="text"
                name="food"
                class="form-control-sm"
                bind:value={modifiedPlayground.food}
            />

            <label for="toilet">Toalett</label>
            <input
                type="text"
                name="toilet"
                class="form-control-sm"
                bind:value={modifiedPlayground.toilet}
            />

            <label for="directions">Ta sig hit</label>
            <input
                type="text"
                name="directions"
                class="form-control-sm"
                bind:value={modifiedPlayground.directions}
            />

            <label for="parking">Parkering</label>
            <input
                type="text"
                name="parking"
                class="form-control-sm"
                bind:value={modifiedPlayground.parking}
            />

            <label class="checkbox-label">
                <input type="checkbox" bind:checked={modifiedPlayground.recommended}>
                Rekommenderad
            </label>
        </div>

        <div class="form-group">
            <div class="label">Funktioner</div>

            {#if modifiedPlayground.state == "draft"}
                <div class="features">
                    <PlaygroundFeatureList
                        bind:features={modifiedPlayground.features}
                        placeholder="-"
                    />
                </div>
            {:else}
                <PlaygroundEditFeatureList
                    bind:features={modifiedPlayground.features}
                />
            {/if}
        </div>

        <div class="form-group">
            <label for="url">URL för mer information</label>
            <input
                type="text"
                name="url"
                class="form-control-sm"
                bind:value={modifiedPlayground.url}
            />

            <label for="operator">Driftsansvar</label>
            <OperatorDropdown name="operator" bind:value={operatorId} />
        </div>
    </div>

    <!-- Whether a new playground is believable often comes down to whether it was sent in
         with photos. They are here to judge the playground by rather than to be handled one
         by one: approving it publishes them along with it, and any that don't belong can be
         rejected afterwards from the popup of the published playground. Imported drafts have
         never been to the server and can't have any. Published playgrounds are left out,
         since their photos are shown in the popup this dialog is opened from. -->
    {#if playground._id && modifiedPlayground.state == "draft"}
        <div class="photos">
            <PlaygroundPhotoGallery {playground} readonly />
        </div>
    {/if}

    <div class="button-wrapper-wrapper">
        {#if modifiedPlayground.state == "draft"}
            <div class="button-wrapper">
                <button class="btn btn-sm" on:click={cancel}>Avbryt</button>
                <ActionButton
                    {busy}
                    action={reject}
                    variant="btn-warning"
                    title="Avslå lekplatsen"><u>A</u>vslå</ActionButton
                >
                <ActionButton
                    {busy}
                    action={approve}
                    name="approve"
                    title="Godkänn lekplatsen">Godkänn</ActionButton
                >
            </div>
        {:else}
            <div class="button-wrapper">
                <button class="btn btn-sm" on:click={cancel}>Avbryt</button>
                <ActionButton {busy} action={save} title="Spara ändringarna"
                    >Spara</ActionButton
                >
            </div>
        {/if}
    </div>
</div>

<style>
    /* With the photos in, the dialog is taller than the window on a laptop screen. The
       fields scroll instead, so that what the playground is judged by stays where it is:
       its name at the top, and the photos and the buttons at the bottom. */
    .dialog {
        display: flex;
        flex-direction: column;
        /* Leaves room for the toolbar above the map, and for the tip pointing at the
           marker, so that Leaflet can fit the whole dialog inside the map. Measured
           against the dynamic viewport, so the dialog isn't cut off by the browser chrome
           of a phone. */
        max-height: calc(100vh - 160px);
        max-height: calc(100dvh - 160px);
        width: 100%;
    }

    .header,
    .photos,
    .button-wrapper-wrapper {
        flex-shrink: 0;
    }

    .fields {
        overflow-y: auto;
        /* A flex item won't shrink below its content without this */
        min-height: 0;
        /* Scrolling one axis clips the other, which cuts the sides off the focus ring of a
           field as wide as the box. The negative margin widens what clips, and the padding
           puts the fields back where they stood. */
        margin: 0 -4px;
        padding: 0 4px;
    }

    label {
        margin-bottom: 0;
    }

    .form-control-sm {
        width: 100%;
        font-size: 12px;
    }

    textarea {
        height: 75px;
    }

    .checkbox-label {
        display: block;
        margin: 8px 0;
    }

    .checkbox-label input {
        margin-right: 0.5em;
    }

    .button-wrapper-wrapper {
        margin-top: 15px;
    }

    .button-wrapper {
        text-align: right;
    }

    .features {
        max-height: 100px;
        overflow-y: scroll;
        /* Room for the focus ring of a checkbox, as above */
        margin: 0 -4px;
        padding: 0 4px;
    }
</style>
